import React, { useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { EditableList } from '../components/Editable';
import { useProducts } from '../product/ProductContext';

const DEFAULT_VOICE = { summary: '', formality: 30, seriousness: 35, avoid: [], notes: '' };

const Slider = ({ left, right, value, onChange, label }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="ls-slider-labels"><span>{left}</span><span>{right}</span></div>
        <input
            type="range"
            className="ls-range"
            min={0}
            max={100}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            aria-label={label}
        />
    </div>
);

const BrandVoiceScreen = () => {
    const { currentProduct: product, updateProduct } = useProducts();
    const [voice, setVoice] = useState({ ...DEFAULT_VOICE, ...product.voice });
    const [isSaving, setIsSaving] = useState(false);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const saved = { ...DEFAULT_VOICE, ...product.voice };
    const isDirty = JSON.stringify(voice) !== JSON.stringify(saved);

    const update = (changes) => {
        setVoice(prev => ({ ...prev, ...changes }));
        setNotice('');
    };

    const handleSave = async () => {
        if (!voice.summary.trim()) {
            setError('Describe the voice in a few words first.');
            return;
        }
        setIsSaving(true);
        setError('');
        try {
            await updateProduct(product.id, { voice: { ...voice, summary: voice.summary.trim() }, setupDone: { voice: true } });
            setNotice('Saved. Every post, plan and ad now uses this voice.');
        } catch (e) {
            setError("Couldn't save the voice. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSuggest = async () => {
        setIsSuggesting(true);
        setError('');
        try {
            const suggested = await api.suggestVoice(product.id);
            update({
                summary: suggested.summary || voice.summary,
                formality: suggested.formality ?? voice.formality,
                seriousness: suggested.seriousness ?? voice.seriousness,
                avoid: suggested.avoid?.length ? suggested.avoid : voice.avoid,
                notes: suggested.notes || voice.notes,
            });
            setNotice('Suggested from your product profile and ICPs. Tweak it, then save.');
        } catch (e) {
            setError("Couldn't suggest a voice. Please try again.");
        } finally {
            setIsSuggesting(false);
        }
    };

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">{product.name}</div>
                    <h1 className="ls-page-title">Brand voice</h1>
                </div>
                <div className="ls-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={handleSuggest} disabled={isSuggesting}>
                        {isSuggesting ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={18} />}
                        {isSuggesting ? 'Suggesting…' : 'Suggest from profile'}
                    </button>
                    <button type="button" className="ls-btn ls-btn-primary" onClick={handleSave} disabled={isSaving || (!isDirty && product.setupDone?.voice)}>
                        {isSaving ? 'Saving…' : 'Save voice'}
                    </button>
                </div>
            </div>

            {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}
            {notice && <div className="ls-notice">{notice}</div>}

            <div className="ls-two-col">
                <div className="ls-col">
                    <div className="ls-card" style={{ padding: 22, gap: 16 }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span className="ls-card-title">In a few words</span>
                            <input
                                className="ls-input"
                                value={voice.summary}
                                onChange={(e) => update({ summary: e.target.value })}
                                placeholder="e.g. Warm, practical, lightly funny"
                            />
                        </label>
                        <Slider left="Casual" right="Formal" value={voice.formality} label="Casual to formal" onChange={(formality) => update({ formality })} />
                        <Slider left="Playful" right="Serious" value={voice.seriousness} label="Playful to serious" onChange={(seriousness) => update({ seriousness })} />
                    </div>
                    <div className="ls-card" style={{ padding: 22, gap: 10 }}>
                        <div className="ls-card-title">More guidance</div>
                        <textarea
                            className="ls-textarea"
                            rows={5}
                            value={voice.notes}
                            onChange={(e) => update({ notes: e.target.value })}
                            placeholder="e.g. Talk like a friend who's a nurse. Short sentences. Emoji sparingly. Never shame food choices."
                        />
                    </div>
                </div>
                <div className="ls-col">
                    <div className="ls-card" style={{ padding: 22, gap: 10 }}>
                        <div className="ls-card-title">Words to avoid</div>
                        <div style={{ fontSize: 13, color: 'var(--muted)' }}>The AI never uses these.</div>
                        <EditableList
                            items={voice.avoid}
                            quote
                            placeholder="e.g. hack"
                            label="words to avoid"
                            onSave={(avoid) => update({ avoid })}
                        />
                    </div>
                    <div className="ls-card" style={{ padding: 22, gap: 8, background: 'var(--page)', boxShadow: 'none' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>Where it's used</div>
                        <div style={{ fontSize: 14, color: 'var(--secondary)', lineHeight: 1.5 }}>
                            Hooks, captions, blog articles, ad copy, video scripts and weekly content plans for {product.name}.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BrandVoiceScreen;
